import React from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import {
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  WIDTH_LADDERS,
  faqContentToItems,
  getCloudinarySrcSet,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import contentApi from 'data/api/content';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';

import Breadcrumb from '../components/breadcrumb';
import CalendarCards from '../components/calendars/calendarCards';
import UpClose from '../components/calendars/upClose';
import LandingEditorial from '../components/landing/editorial';
import {
  Picture,
  PriceTag,
  Responsive,
  SectionHead,
  ctaTracker,
  productImage,
} from '../components/landing/shared';
import { Head, Layout } from '../components/layout';
import NewsletterTile from '../components/newsletter/newsletterTile';
import { ProductDetailLink } from '../components/pageLinks';
import PdpIcon from '../components/product/pdpIcon';
import ProductFaq from '../components/product/productFaq';
import { isPriced } from '../lib/displayPrices.mjs';
import { productPrice } from '../lib/landingPrice';
import SETTINGS from '../settings';

// Calendars category page (docs/calendars-page.md). Explicit page file, so it wins over the
// category route in [...params].js. Title, meta, H1, FAQ and the editorial block come from
// content/categories/calendars.md, prices from the catalog, the rest is design copy.
const CATEGORY_ID = 'calendars';
const COMPARE_ID = 'compare';
const track = ctaTracker('calendars_page');

const HERO_IMAGE = { imageKey: 'carousel/2026_calendar', widths: WIDTH_LADDERS.carousel };
const HERO_IMAGE_SIZES = '(min-width: 992px) 640px, 100vw';

// Card titles are the current PDP names. Specs come from content/products/*.md.
const CALENDARS = [
  {
    productId: 'wall-calendar',
    name: 'Keepsake Wall Calendar',
    short: 'Keepsake',
    need: { desktop: 'Keep the photos after each month', phone: 'Keep the photos' },
    size: '8.25x12 in',
    sizeDetail: '21.0 × 30.5 cm · portrait',
    specs: [
      { icon: 'image', label: 'Photos', value: '14 photos, one per month' },
      { icon: 'calendar', label: 'Months', value: '14 months, you pick the first one' },
      { icon: 'leaf', label: 'Paper', value: 'Premium matte, FSC certified' },
    ],
    highlight: {
      icon: 'scissors',
      text: 'Perforated photos. Tear one off when the month ends and keep it.',
    },
    glance: { photos: '14', starts: 'Any month', perforated: 'Yes' },
    image: productImage('wall-calendar/1'),
    thumb: productImage('wall-calendar/2'),
  },
  {
    productId: 'wall-calendar-large',
    name: 'Large Wall Calendar',
    short: 'Large',
    need: { desktop: 'Room to write every date', phone: 'Room to write' },
    size: '11x17 in',
    sizeDetail: '27.9 × 43.2 cm · portrait',
    specs: [
      { icon: 'image', label: 'Photos', value: '14 photos, one per month' },
      { icon: 'calendar', label: 'Months', value: '14 months, you pick the first one' },
      { icon: 'leaf', label: 'Paper', value: 'Premium matte, FSC certified' },
    ],
    highlight: {
      icon: 'penLine',
      text: 'The most room to write. Big date boxes for birthdays, plans and reminders.',
    },
    glance: { photos: '14', starts: 'Any month' },
    image: productImage('wall-calendar-large/1'),
    thumb: productImage('wall-calendar-large/3'),
  },
  {
    productId: 'engineer-print-calendar',
    name: 'Engineer Print Calendar',
    short: 'Engineer',
    need: { desktop: 'The whole year on one sheet', phone: 'Whole year, one sheet' },
    size: '3x4 ft',
    sizeDetail: '36x48 in · 91.4 × 121.9 cm · portrait',
    specs: [
      { icon: 'image', label: 'Photos', value: '1 to 12 photos, three templates' },
      // The calendar year changes every season, see docs/calendars-page.md.
      { icon: 'calendar', label: 'Months', value: '2027, January to December' },
      { icon: 'leaf', label: 'Paper', value: 'Extra-light 20 lb engineer paper' },
    ],
    highlight: {
      icon: 'layoutGrid',
      text: 'The whole year on one sheet. Hang it with 36 in Wood Poster Rails.',
    },
    glance: { photos: '1 to 12', starts: 'Jan 2027' },
    image: productImage('engineer-print-calendar/3'),
    thumb: productImage('engineer-print-calendar/3'),
  },
];

const DETAILS = [
  {
    tag: 'Keepsake',
    title: 'Tear-off photos',
    body: 'Each month’s photo is perforated. Tear it off when the month ends and keep it.',
    image: productImage('wall-calendar/5'),
  },
  {
    tag: 'Keepsake and Large',
    title: 'Premium matte paper',
    body: 'Certified by the Rainforest Alliance Program for FSC Standards.',
    image: productImage('wall-calendar-large/6'),
  },
  {
    tag: 'Keepsake and Large',
    title: 'Spiral bound',
    body: 'Flip to the next month and the photo above the dates changes with it.',
    image: productImage('wall-calendar-large/5'),
  },
  {
    tag: 'Engineer Print',
    title: 'Poster scale, one sheet',
    body: 'Extra-light 20 lb paper at 36x48 in. Use your biggest original files.',
    image: productImage('engineer-print-calendar/5'),
  },
];

const STEPS = [
  {
    title: 'Pick a format',
    body: 'Keepsake, Large or Engineer Print. Open its page to start your design.',
  },
  {
    title: 'Add your photos',
    body: '14 photos for a wall calendar, 1 to 12 for the Engineer Print Calendar. JPG, PNG or HEIC, straight from your phone or computer.',
  },
  {
    title: 'Preview, then order',
    body: 'Check every month before you buy. Wall calendars start in the month you choose. We point out photos that may print blurry.',
  },
];

const CalendarsPage = ({ content }) => {
  const dataContext = useDataContext();
  const { previewSummary } = useClaimedCoupon();

  const category = dataContext.getCategory(CATEGORY_ID);
  const breadcrumb = [{ name: 'Home', url: '/' }, { name: category.name }];
  const calendars = CALENDARS.map((calendar) => ({
    ...calendar,
    product: dataContext.getProduct(calendar.productId),
  }))
    .filter(({ product }) => isPriced(product))
    .map((calendar) => ({ ...calendar, price: productPrice(calendar.product, previewSummary) }));
  // Same coupon-aware amount as the cards, so the hero never shows a different "from" price.
  const cheapest = calendars.reduce(
    (min, calendar) => (!min || calendar.price.amount < min.price.amount ? calendar : min),
    null,
  );

  return (
    <>
      <Head
        title={category.seoTitle ?? category.name}
        description={category.seoDescription ?? category.shortDescription}
        image={content.seoUrl || getCloudinaryUrl({ imageKey: category.image })}
        heroImage={getCloudinaryUrl({ imageKey: HERO_IMAGE.imageKey })}
        heroImageSrcSet={getCloudinarySrcSet(HERO_IMAGE)}
        heroImageSizes={HERO_IMAGE_SIZES}
      />
      <ScriptMarkupBreadcrumb items={breadcrumb} host={SETTINGS.HOST} />
      {content.faq && <ScriptMarkupFaq items={faqContentToItems(content.faq)} />}

      <Layout>
        <div className="lnd cal">
          {/* One DOM for both layouts: phones move the photo on top and the CTA last. */}
          <section className="cal-hero">
            <Container className="cal-hero-inner">
              <div className="cal-hero-photo">
                <Picture
                  image={HERO_IMAGE}
                  sizes={HERO_IMAGE_SIZES}
                  alt="A custom photo wall calendar open to June hangs above a desk with a tablet and plants."
                  priority
                />
              </div>
              <div className="cal-hero-copy">
                <Breadcrumb items={breadcrumb} />
                <h1 className="lnd-h1">{category.heroTitle ?? category.name}</h1>
                <p className="lnd-lead">
                  Turn your favorite photos into a calendar. Compare formats and find the one that
                  fits your space.
                </p>
              </div>
              <div className="cal-hero-actions">
                <a href={`#${COMPARE_ID}`} className="lnd-btn" onClick={track('compare', 'hero')}>
                  Choose Your Calendar
                  <PdpIcon name="arrowDown" size={16} />
                </a>
                {cheapest && (
                  <p className="cal-hero-note lnd-desk">
                    From <PriceTag price={{ ...cheapest.price, priceFrom: false }} /> ·{' '}
                    {calendars.length} sizes
                  </p>
                )}
              </div>
              {calendars.length > 0 && (
                <div className="cal-quick">
                  <p className="cal-quick-label lnd-desk">Pick by what you need</p>
                  <ul className="cal-quick-list">
                    {calendars.map(({ productId, name, need, size, price, thumb }) => (
                      <li key={productId}>
                        <ProductDetailLink productId={productId}>
                          <a
                            className="cal-quick-item"
                            onClick={track('product', 'quick_pick', productId)}
                          >
                            <span className="cal-quick-thumb">
                              <Picture image={thumb} sizes="76px" alt="" />
                            </span>
                            <span className="cal-quick-text">
                              <span className="cal-quick-need">
                                <Responsive desktop={need.desktop} phone={need.phone} />
                              </span>
                              <span className="cal-quick-name">{name}</span>
                              <span className="cal-quick-meta">
                                {size} · <PriceTag price={price} />
                              </span>
                            </span>
                            <PdpIcon name="arrowRight" size={20} className="cal-quick-arrow" />
                          </a>
                        </ProductDetailLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Container>
          </section>

          {calendars.length > 0 && (
            <CalendarCards id={COMPARE_ID} calendars={calendars} track={track} />
          )}

          <UpClose photo={productImage('wall-calendar-large/3')} details={DETAILS} />

          <section className="lnd-section cal-how">
            <Container>
              <SectionHead
                eyebrow="How ordering works"
                title="From your camera roll to your wall"
              />
              <ol className="cal-steps">
                {STEPS.map((step, index) => (
                  <li key={step.title} className="cal-step">
                    <span className="cal-step-number">{String(index + 1).padStart(2, '0')}</span>
                    <h3 className="cal-step-title">{step.title}</h3>
                    <p className="cal-step-body">{step.body}</p>
                  </li>
                ))}
              </ol>
              {/* Testimonial from content/products/wall-calendar-large.md. */}
              <figure className="cal-quote">
                <PdpIcon name="quote" size={28} />
                <blockquote>
                  “Created a Calendar using Parabo Press - such great quality and shipped fast!”
                </blockquote>
                <figcaption>@megsteeledesign on Instagram</figcaption>
              </figure>
            </Container>
          </section>

          {content.faq && (
            <section className="lnd-section cal-faq">
              <Container>
                <ProductFaq
                  eyebrow="Help choosing"
                  title="Photo calendar questions"
                  intro={
                    <>
                      Still unsure? Write to{' '}
                      <a href="mailto:help@parabo.press">help@parabo.press</a>.
                    </>
                  }
                  items={content.faq}
                />
              </Container>
            </section>
          )}

          <LandingEditorial
            eyebrow="About our calendars"
            title="Custom photo calendars from Parabo Press"
            html={content.extraDescription}
          />

          <NewsletterTile />
        </div>
      </Layout>
    </>
  );
};

CalendarsPage.getInitialProps = async () => ({
  content: await contentApi.category(CATEGORY_ID),
});

CalendarsPage.propTypes = {
  content: PropTypes.object.isRequired,
};

export default CalendarsPage;
